/** @format */

import { observable, action, computed } from 'mobx';
import { newClient, newClientAddress } from '../models/clients';
import { getTimestamp } from '../helpers/getTimestamp';
import { getPhoneNumberFromString } from '../helpers/getPhoneNumberFromString';
import database from '../firebase/config';
import RequestsStore from './RequestsStore';
import sortClients from '../helpers/sortClients';

let clientsSearchTimeout;

class ClientsStore {
  @observable openedClients = [];
  @observable clients = {};
  @observable clientsParams = {
    tableKeys: [
      'NOME',
      'TELEFONO',
      'EMAIL',
      'VENDITE',
      'VALORE VENDITE',
      'ATTIVO DA',
    ],
    clientLoaded: false,
    clientRemoved: false,
    quickSearchValue: '',
    quickSearchResults: [],
    dropdowns: {
      clientAddresses: [],
    },
    advancedSearch: {
      active: false,
      prop: 'surname',
      value: '',
    },
    clientEditForm: {
      open: false,
    },
    clientAddressForm: {
      open: false,
      clientId: null,
      addressId: null,
    },
    isClientPage: false,
    searchValue: '',
    searchNotFound: false,
    itemLoaded: 50,
    itemsToLoad: 50,
    filter: {
      active: false,
      progress: null,
    },
    orderBy: {
      prop: 'timestamp',
      direction: 'asc',
    },
  };
  @observable currentClient = {
    data: {
      avatarLink: '',
      name: '',
      surname: '',
      fiscalCode: '',
      vatNumber: '',
      email: '',
      mobPhone: '',
      landPhone: '',
      badPayer: false,
      lastPurchase: '',
      requestsNum: 0,
      salesNum: 0,
      salesValue: 0,
      notes: '',
      status: 'attivo',
      timestamp: '',
      type: '',
    },
    requests: {},
    addresses: {},
    purchases: {},
  };
  @observable currentAddress = {
    data: {
      main: false,
      address: '',
      address2: '',
      zip: '',
      city: '',
      state: '',
      country: '',
      notes: '',
    },
  };
  @observable clientsErrors = {
    client: [],
  };

  @observable fetching = {
    clients: {
      loading: false,
      timeout: undefined,
    },
  };

  @action fetchClients = () => {
    let clientsRef;
    const searchProp = this.clientsParams.advancedSearch.prop;
    const searchValue = this.clientsParams.advancedSearch.value;
    if (!this.clientsParams.advancedSearch.active) {
      clientsRef = database
        .ref('/clients')
        .orderByChild('data/status')
        .equalTo('attivo')
        .limitToLast(this.clientsParams.itemLoaded);
    } else {
      clientsRef = database
        .ref('/clients')
        .orderByChild('data/' + searchProp)
        .equalTo(searchValue);
    }
    clientsRef.off('value');
    clientsRef.on('value', clientsSnapshot => {
      const clientsValue = clientsSnapshot.val();
      if (clientsValue !== undefined && clientsValue !== null) {
        this.clients = clientsValue;
      }
    });
  };

  @action searchClient = value => {
    this.clientsParams.searchValue = value;
  };

  @action advancedSearch = () => {
    this.fetchClients();
  };

  @action openAdvancedSearch = () => {
    if (this.clientsParams.advancedSearch.active === true) {
      this.clientsParams.advancedSearch.active = false;
    } else {
      this.clientsParams.advancedSearch.active = true;
    }
    this.fetchClients();
  };

  @action advancedSearchPropSelect = result => {
    this.clientsParams.advancedSearch.prop = this.getOriginalPropName(
      result.children,
    );
  };

  @action advancedSearchInput = value => {
    this.clientsParams.advancedSearch.value = value;

    if (typeof clientsSearchTimeout !== 'undefined') {
      clearTimeout(clientsSearchTimeout);
    }

    clientsSearchTimeout = setTimeout(() => {
      this.fetchClients();
    }, 1000);
  };

  @action getOriginalPropName = value => {
    let translator = {
      cognome: 'surname',
      cellulare: 'mobPhone',
      telefono: 'landPhone',
      email: 'email',
    };
    return translator[value];
  };

  @action assignKey = () => {
    let clientsKeys = Object.keys(this.clients);
    let clientsValues = Object.values(this.clients);
    clientsValues.forEach((clients, index) => {
      clients.data.key = clientsKeys[index];
    });
  };

  @action loadMoreClients = () => {
    this.clientsParams.itemLoaded += this.clientsParams.itemsToLoad;
    this.fetchClients();
  };

  @action sortClientsBy = key => {
    let prop = null;
    let clientsParams = this.clientsParams;
    this.clientsParams = sortClients(key, clientsParams, prop);
  };

  @action handleClientSearch = value => {
    if (!value || value === '' || value.length < 3) {
      this.clientsParams.quickSearchResults = [];
      return;
    }

    const doneTyping = () => {
      this.fetching.clients.loading = true;

      let results = [];

      const clientsRef = database.ref('/clients/');
      clientsRef.once('value').then(clientsSnapshot => {
        this.fetching.clients.loading = false;

        if (!clientsSnapshot.exists()) return;

        const clientsValues = clientsSnapshot.val();

        for (const [key, client] of Object.entries(clientsValues)) {
          if (!client.data) continue;

          const fullName = `${client.data.name} ${client.data.surname}`;

          if (fullName.toLowerCase().includes(value.toLowerCase())) {
            // Limit to 50 results at most
            if (results.length > 50) {
              break;
            }

            results.push({
              title: `${fullName} (${client.data.mobPhone || '-'})`,
              id: key,
              key: key,
            });
          }
        }

        this.clientsParams.quickSearchResults = results;
      });
    };

    clearTimeout(this.fetching.clients.timeout);
    this.fetching.clients.timeout = setTimeout(doneTyping.bind(this), 2000);
  };

  @action handleSelectClient = clientId => {
    this.loadCurrentClient(clientId, true);
  };

  @action handleClientInput = (name, value) => {
    this.currentClient.data[name] = value;
  };

  @action formatClientData = clientData => {
    if (!clientData) {
      return clientData;
    }

    if (clientData.landPhone) {
      clientData.landPhone = getPhoneNumberFromString(clientData.landPhone);
    }

    if (clientData.mobPhone) {
      clientData.mobPhone = getPhoneNumberFromString(clientData.mobPhone);
    }

    return clientData;
  };

  @action handleClientSubmit = clientId => {
    if (clientId === null || clientId === undefined) {
      let clientRef = database.ref('/clients');
      this.currentClient.data.timestamp = getTimestamp();
      this.currentClient.data = this.formatClientData(this.currentClient.data);
      this.clientsParams.clientLoaded = true;
      clientRef.push(this.currentClient).then(clientSnapshot => {
        let newClientKey = clientSnapshot.key;
        this.clientsParams.currentClientId = newClientKey;
        RequestsStore.setRequestClient(
          newClientKey,
          this.currentClient.data.name,
          this.currentClient.data.surname,
        );
      });
    } else {
      this.currentClient.data = this.formatClientData(this.currentClient.data);
      let clientRef = database.ref('/clients/' + clientId + '/data/');
      clientRef.set(this.currentClient.data).then(snapshot => {
        this.toggleEditClientForm();
      });
    }
  };

  @action toggleEditClientForm = () => {
    this.clientsParams.clientEditForm.open =
      !this.clientsParams.clientEditForm.open;
  };

  @action loadCurrentClient = (clientId, loadingRequest) => {
    let clientRef = database.ref('/clients/' + clientId);

    clientRef.on('value', clientSnapshot => {
      this.openedClients.forEach(id => {
        if (id !== clientId) {
          database.ref(`/clients/${id}`).off('value');
        }
      });
      this.openedClients = [clientId];
      let clientValue = clientSnapshot.val();
      if (clientValue !== undefined && clientValue !== null) {
        this.clientsParams.clientLoaded = true;
        this.clientsParams.currentClientId = clientId;
        this.currentClient.data = clientValue.data;
        this.currentClient.purchases = clientValue.purchases;
        this.currentClient.addresses = clientValue.addresses;
        this.currentClient.requests = clientValue.requests;
        if (
          clientValue.addresses === undefined ||
          clientValue.addresses === null
        ) {
          this.currentClient.addresses = {};
        } else {
          let clientAddressKeys = Object.keys(clientValue.addresses);
          this.clientsParams.dropdowns.clientAddresses = [];
          clientAddressKeys.forEach(key => {
            const data = clientValue.addresses[key].data;
            this.clientsParams.dropdowns.clientAddresses.push({
              text: `${data.address} ${data.address2},${data.zip},${data.city}, ${data.country}`,
              value: key,
            });
          });
        }

        if (
          clientValue.requests === undefined ||
          clientValue.requests === null
        ) {
          this.currentClient.requests = {};
        }

        if (
          clientValue.purchases === undefined ||
          clientValue.purchases === null
        ) {
          this.currentClient.purchases = {};
        }

        if (loadingRequest) {
          RequestsStore.setRequestClient(
            clientId,
            this.currentClient.data.name,
            this.currentClient.data.surname,
          );
        }
      } else {
        this.clients.clientRemoved = true;
      }
    });
  };

  @action resetCurrentClient = () => {
    this.currentClient.data = { ...newClient.data };
    this.currentClient.requests = { ...newClient.requests };
    this.currentClient.addresses = { ...newClient.addresses };
    this.currentClient.purchases = { ...newClient.purchases };
    this.clientsParams.clientEditForm.open = false;
    this.clientsParams.clientAddressForm.open = false;
    this.clientsParams.clientAddressForm.clientId = null;
    this.clientsParams.clientAddressForm.addressId = null;
    this.clientsParams.clientLoaded = false;
    this.clientsParams.currentClientId = null;
    this.clientsParams.clientRemoved = false;
  };

  @action handleEditAddress = (name, value) => {
    this.currentAddress.data[name] = value;
  };

  @action toggleAddressForm = (clientId, addressId) => {
    this.clientsParams.clientAddressForm.open =
      !this.clientsParams.clientAddressForm.open;
  };

  @action loadCurrentAddress = (clientId, addressId) => {
    this.clientsParams.clientAddressForm.clientId = clientId;
    this.clientsParams.clientAddressForm.addressId = addressId;
    this.currentAddress = this.currentClient.addresses[addressId];
  };

  @action resetCurrentAddress = () => {
    this.clientsParams.clientAddressForm.clientId = null;
    this.clientsParams.clientAddressForm.addressId = null;
    this.currentAddress.data = { ...newClientAddress.data };
  };

  @action handleAddressSubmit = () => {
    let clientId = this.clientsParams.currentClientId;
    let addressId = this.clientsParams.clientAddressForm.addressId;
    let clientAddresses = this.currentClient.addresses;
    let clientAddressKeys = Object.keys(clientAddresses);
    let newAddressId = addressId;

    if (addressId === null) {
      newAddressId =
        clientAddressKeys.length > 0
          ? Number(clientAddressKeys[clientAddressKeys.length - 1]) + 1
          : 0;
    }

    let addressRef = database.ref(
      `/clients/${clientId}/addresses/${newAddressId}`,
    );
    addressRef.set(this.currentAddress).then(() => {
      this.toggleAddressForm();
      this.resetCurrentAddress();
    });
  };

  @computed get clientFullName() {
    return `${this.currentClient.data.name} ${this.currentClient.data.surname}`;
  }
}

export default new ClientsStore();
